/**
 * Créditos en la web de OPUS ai (1 crédito = 10,000 tokens).
 *
 * El formato, la recarga a la medida, factores y promedios viven en `@supernova/shared` (misma lógica
 * que la API y el worker, mismos vectores que portales y bi). Aquí solo van los
 * ayudantes que dependen de la respuesta de `/api/v1/usage`.
 */
import {
  aCreditos,
  calcularPromedios,
  normalizarFactores,
  etiquetaCreditos,
  formatCreditos,
  formatTokens,
  PANTEON_DEFAULT,
  type ConfigPanteon,
  type Promedio,
} from '@supernova/shared';
import type { UsageResponse } from '../api/usage.js';

export {
  aCreditos,
  etiquetaCreditos,
  formatCreditos,
  formatTokens,
  panteonValido,
  precioPanteon,
  precioCreditoPanteon,
  sugerirPaquete,
  TOKENS_POR_CREDITO,
} from '@supernova/shared';

export const NOTA_EQUIVALENCIA = '1 crédito = 10,000 tokens';

/** ¿Se le habla al cliente en créditos? (CREDITOS_ENABLED del API, vía `unidad.activo`). */
export function enCreditos(usage: Pick<UsageResponse, 'unidad'> | null | undefined): boolean {
  return usage?.unidad?.activo === true;
}

/** Promedios (por operación) que manda eko27 o, si no llegan, los medidos × factor local. */
export function promediosDe(usage: Pick<UsageResponse, 'promedios' | 'factores'> | null | undefined): Promedio[] {
  const api = usage?.promedios;
  if (api && api.length) {
    return api.map((p) => ({
      id: p.id,
      label: p.label,
      unidad: p.unidad,
      factor_clave: 'default',
      tokens_reales: 0,
      tokens: p.tokens,
      creditos: p.creditos,
      creditos_aprox: p.creditos_aprox,
      texto: p.texto,
    }));
  }
  return calcularPromedios(normalizarFactores(usage?.factores));
}

/** "≈ 0.3 créditos por concepto (promedio)" para un id de promedio ('ia_completa', 'apu', …). */
export function textoPromedio(
  usage: Pick<UsageResponse, 'promedios' | 'factores'> | null | undefined,
  id: string,
): string {
  return promediosDe(usage).find((p) => p.id === id)?.texto ?? '';
}

/** Configuración de la recarga a la medida (la de eko27 o la de respaldo). */
export function panteonDe(usage: Pick<UsageResponse, 'panteon'> | null | undefined): ConfigPanteon {
  const p = usage?.panteon;
  if (p && p.tramos?.length) {
    return { id: p.id, nombre: p.nombre, min: p.min, max: p.max, paso: p.paso, tramos: p.tramos };
  }
  return PANTEON_DEFAULT;
}

/** Créditos de una recarga (los que manda el API o tokens/10,000). */
export function creditosDeKit(k: { tokens: number; creditos?: number }): number {
  return Number.isFinite(k.creditos) && (k.creditos as number) > 0 ? (k.creditos as number) : aCreditos(k.tokens);
}

/** Desglose bolsa/usado/recargas/disponible en TOKENS (el formato lo decide el componente). */
/**
 * Pastel del chip de créditos (igual que el del hub): % DISPONIBLE de lo que había en el mes
 * (bolsa + recargas) y nivel con el semáforo de siempre sobre la bolsa usada (≥75 % 'warn',
 * ≥90 % 'bad').
 */
export function pastelDelSaldo(saldo: {
  bolsa: number;
  usado: number;
  recargas: number;
  disponible: number;
  pct: number;
}): { pct: number; nivel: '' | 'warn' | 'bad' } {
  const total = saldo.bolsa + saldo.recargas;
  const pct = total > 0 ? Math.max(0, Math.min(100, (saldo.disponible / total) * 100)) : 0;
  return {
    pct: Math.round(pct * 10) / 10,
    nivel: saldo.pct >= 90 ? 'bad' : saldo.pct >= 75 ? 'warn' : '',
  };
}

export function saldoEnTokens(usage: UsageResponse | null | undefined): {
  bolsa: number;
  usado: number;
  recargas: number;
  disponible: number;
  pct: number;
} | null {
  if (!usage?.tokens) return null;
  const { quota, used, remaining, pack_tokens } = usage.tokens;
  if (!quota) return null;
  const pct = quota > 0 ? Math.min(100, (used / quota) * 100) : 0;
  return { bolsa: quota, usado: used, recargas: pack_tokens ?? 0, disponible: remaining, pct };
}
